import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getCorrectEnums } from '../../../utils/Functions';
import { UserService } from '../UserService';
import { IUser } from '../../../model/user/IUser';
import { getCountryName } from '../../../utils/helpers/CorrectCountryNames';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import { ErrorToast } from '../../../utils/toasters';
import { FilterActions } from '../../../shared/components/FilterActions';
import SingleSelectDropdown, { ISingleSelectOption } from '../../../shared/components/SingleSelectDropdown';
interface IUserFilterProps {
    handleChanges: (
        evt: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        isSelectBoolean?: boolean
    ) => void;
    handleClicks: () => void;
    setState: React.Dispatch<React.SetStateAction<any>>;
    allUsers?: boolean;
    page?: string;
    onClear?: () => void;
    hasActiveFilters?: boolean;
}
export function UserFilter({
    handleChanges,
    handleClicks,
    allUsers,
    setState,
    page,
    onClear,
    hasActiveFilters,
}: IUserFilterProps) {
    const { userType, country, gender, campaignCategory } = useSelector((state: any) => state.enums);
    const [agencies, setAgencies] = useState<IUser[]>([]);
    const [agencyFilter, setAgencyFilter] = useState('');
    const [selectedValues, setSelectedValues] = useState<Record<string, string>>({
        sortCriteria: 'createdAt',
        sortType: 'DESC',
    });

    const handleSelect = (name: string, value: string, isBoolean = false) => {
        setSelectedValues(prev => ({ ...prev, [name]: value }));
        handleChanges(
            { target: { name, value: isBoolean ? value || 'null' : value } } as React.ChangeEvent<HTMLSelectElement>,
            isBoolean ? true : undefined
        );
    };

    const renderSelect = (
        name: string,
        placeholder: string,
        options: ISingleSelectOption[],
        { isBoolean = false, clearable = true, withImage = false } = {}
    ) => (
        <SingleSelectDropdown
            placeholder={placeholder}
            options={options}
            selected={selectedValues[name] ?? ''}
            onChange={value => handleSelect(name, value, isBoolean)}
            clearable={clearable}
            withImage={withImage}
        />
    );

    const enumOptions = (values: string[]): ISingleSelectOption[] =>
        values.map(value => ({ value, label: getCorrectEnums(value) }));

    useEffect(() => {
        UserService.getAgencies({ displayName: agencyFilter, perPage: 15 })
            .then(data => {
                setAgencies(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [agencyFilter]);

    return (
        <Form>
            <Row style={{ rowGap: '18px' }}>
                <div className="col-md-2 col-6">
                    <Form.Control
                        placeholder="User id"
                        className="autocomplete-input"
                        name="id"
                        onChange={handleChanges}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <Form.Control
                        placeholder="name,email,handle,phone"
                        className="autocomplete-input"
                        name="search"
                        onChange={handleChanges}
                    />
                </div>
                {allUsers && (
                    <div className="col-md-2 col-6">{renderSelect('userType', 'All types', enumOptions(userType))}</div>
                )}
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'isEnabled',
                        'is Enabled?',
                        [
                            { value: 'enable', label: 'Enabled' },
                            { value: 'disable', label: 'Disabled' },
                        ],
                        { isBoolean: true }
                    )}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'isSuspended',
                        'is Suspended?',
                        [
                            { value: 'enable', label: 'Suspended' },
                            { value: 'disable', label: 'Without suspension' },
                        ],
                        { isBoolean: true }
                    )}
                </div>
                {allUsers && (
                    <div className="col-md-2 col-6">
                        {renderSelect(
                            'isSupervisor',
                            'is Supervisor?',
                            [
                                { value: 'enable', label: 'Supervisor' },
                                { value: 'disable', label: 'Not Supervisor' },
                            ],
                            { isBoolean: true }
                        )}
                    </div>
                )}
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'country',
                        'All countries',
                        country.map((code: string) => ({
                            value: code,
                            label: getCountryName(code),
                            image: `/flags/4x3/${code.toLowerCase()}.svg`,
                        })),
                        { withImage: true }
                    )}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'isOnboarded',
                        'is Onboarded?',
                        [
                            { value: 'enable', label: 'Onboarded' },
                            { value: 'disable', label: 'NOT Onboarded' },
                        ],
                        { isBoolean: true }
                    )}
                </div>
                <div className="col-md-2 col-6">{renderSelect('gender', 'All genders', enumOptions(gender))}</div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'campaignCategory',
                        'All Categories',
                        campaignCategory.map((category: string) => ({
                            value: category,
                            label: getCorrectEnums(category),
                            image: `/icons/categories/${category}.svg`,
                        })),
                        { withImage: true }
                    )}
                </div>
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="Agency name or id"
                        onSelect={setState}
                        setFilter={setAgencyFilter}
                        keyState={'agencyCompanyId'}
                        keyItemSelect={'id'}
                        value={agencyFilter}
                        items={agencies}
                        hasClearBtn
                        renderItems={['id', 'displayName']}
                        clearFunc={() => {
                            setState((prev: any) => ({
                                ...prev,
                                agencyCompanyId: undefined,
                            }));
                            setAgencyFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'isIvConnectedClub',
                        'All fee',
                        [
                            { value: 'enable', label: 'iv Connected Club' },
                            { value: 'disable', label: 'Preferred payment' },
                        ],
                        { isBoolean: true }
                    )}
                </div>
                {!allUsers && page !== 'influencer_fee' && (
                    <Form.Group as={Col} className="col-md-2 col-6 mb-0 ">
                        {renderSelect(
                            'sortCriteria',
                            'Sort by',
                            [
                                { value: 'createdAt', label: 'Sort by Date created' },
                                { value: 'lastLogin', label: 'Sort by Last login' },
                                { value: 'allOffers', label: 'Sort by All Offers' },
                                { value: 'campaigns', label: 'Sort by Campaigns' },
                            ],
                            { clearable: false }
                        )}
                    </Form.Group>
                )}
                {!allUsers && page !== 'influencer_fee' && (
                    <Form.Group as={Col} className="col-md-2 col-6 mb-0 ">
                        {renderSelect(
                            'sortType',
                            'Order',
                            [
                                { value: 'DESC', label: 'DESC' },
                                { value: 'ASC', label: 'ASC' },
                            ],
                            { clearable: false }
                        )}
                    </Form.Group>
                )}
                {allUsers && (
                    <div className="col-md-2 col-6">
                        {renderSelect(
                            'hasMobileLogin',
                            'Has Mobile login?',
                            [
                                { value: 'enable', label: 'Has mobile login' },
                                { value: 'disable', label: 'No mobile login' },
                            ],
                            { isBoolean: true }
                        )}
                    </div>
                )}
            </Row>
            {onClear ? (
                <FilterActions onSearch={handleClicks} onClear={onClear} hasActiveFilters={!!hasActiveFilters} />
            ) : (
                <Button className="mt-4" onClick={() => handleClicks()}>
                    Search
                </Button>
            )}
        </Form>
    );
}
